import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent || '0', 10) || 0;
  }

  set currentCartCount(value) {
    const safeValue = Math.max(0, parseInt(value, 10) || 0);
    this.refs.cartBubbleCount.textContent = String(safeValue);
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);

    this.ensureVisible();
    this.loadStoredCount();
    this.syncCartCount(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  onPageShow = (event) => {
    if (event.persisted) {
      this.syncCartCount(false);
    }
  };

  onCartUpdate = (event) => {
    const itemCount = event.detail?.data?.itemCount ?? 0;
    const source = event.detail?.data?.source ?? '';
    const comingFromProductForm = source === 'product-form-component';

    let nextCount = this.currentCartCount;

    if (comingFromProductForm) {
      nextCount = this.currentCartCount + itemCount;
    } else {
      nextCount = itemCount;
    }

    this.renderCartBubble(nextCount, nextCount > 0);
    this.syncCartCount(false);
  };

  ensureVisible() {
    if (this.refs.cartBubble) {
      this.refs.cartBubble.classList.remove('visually-hidden');
    }

    if (this.refs.cartBubbleCount) {
      this.refs.cartBubbleCount.classList.remove('hidden');
    }
  }

  loadStoredCount() {
    try {
      const stored = sessionStorage.getItem('cart-count');

      if (!stored) {
        if (!this.refs.cartBubbleCount.textContent?.trim()) {
          this.currentCartCount = 0;
        }
        return;
      }

      const parsed = JSON.parse(stored);
      const value = parseInt(parsed.value, 10);

      if (!Number.isNaN(value) && value >= 0) {
        this.currentCartCount = value;
      } else if (!this.refs.cartBubbleCount.textContent?.trim()) {
        this.currentCartCount = 0;
      }
    } catch (_) {
      if (!this.refs.cartBubbleCount.textContent?.trim()) {
        this.currentCartCount = 0;
      }
    }
  }

  async syncCartCount(animate = false) {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });

      if (!response.ok) return;

      const cart = await response.json();
      const itemCount = typeof cart.item_count === 'number' ? cart.item_count : 0;

      if (itemCount !== this.currentCartCount) {
        await this.renderCartBubble(itemCount, animate);
      } else {
        this.saveCartCount(itemCount);
        this.ensureVisible();
      }
    } catch (_) {
      // no-op
    }
  }

  saveCartCount(count) {
    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(count),
        timestamp: Date.now(),
      })
    );
  }

  async renderCartBubble(itemCount, animate = true) {
    this.currentCartCount = itemCount;
    this.ensureVisible();

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
    this.saveCartCount(itemCount);

    if (!animate || itemCount === 0) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  }
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}